import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { resolveLineRegisterCallbackUrl } from "../lib/config";
import { AppError, logError } from "../lib/errors";
import {
  clearPendingLegalConsent,
  LEGAL_CONSENT_REQUIRED_MESSAGE,
  readPendingLegalConsent,
  registrationLegalErrorMessage,
} from "../lib/legalConsent";
import { getValidLineOfficialAccountUrl } from "../lib/lineOfficialAccount";
import { consumeLineRegisterEntrySource, LINE_AUTH_MODE_KEY, readLineRegisterEntrySource } from "../lib/lineRegisterSource";
import { storage } from "../lib/storage";
import { bootstrapAuth } from "../services/auth";

type LineRegisterErrorKind = "generic" | "friendship" | "account_not_found" | "already_registered";

const LINE_FRIENDSHIP_ERROR_TITLE = "LINEで登録するには友だち追加が必要です";
const LINE_FRIENDSHIP_ERROR_BODY =
  "WizMate公式LINEを友だち追加してから、もう一度LINEで登録してください。";
const LINE_FRIENDSHIP_ERROR_NOTE =
  "すでに友だち追加済みの場合は、WizMate公式LINEをブロックしていないか確認してください。";

function getLineRegisterErrorKind(e: unknown): LineRegisterErrorKind {
  if (!(e instanceof AppError)) return "generic";
  if (e.code === "FORBIDDEN") return "friendship";
  if (e.code === "NOT_FOUND") return "account_not_found";
  if (e.code === "CONFLICT") return "already_registered";
  return "generic";
}

function mapCompleteError(e: unknown): string {
  if (e instanceof AppError) {
    switch (e.code) {
      case "BAD_REQUEST":
        return e.message || "セッションが切れました。最初からやり直してください。";
      case "UNAUTHORIZED":
        return "LINE認証に失敗しました。最初からやり直してください。";
      case "FORBIDDEN":
        return "WizMateの公式LINEを友だち追加してから登録してください。";
      case "NOT_FOUND":
        return "このLINEアカウントでの登録が見つかりません。";
      case "CONFLICT":
        return "このLINEアカウントはすでに登録済みです。";
      case "SERVER":
        return "LINEとの通信に失敗しました。しばらく経ってからお試しください。";
      default:
        return "エラーが発生しました。最初からやり直してください。";
    }
  }
  return "エラーが発生しました。最初からやり直してください。";
}

function mapCallbackError(e: unknown): string {
  if (e instanceof AppError) {
    switch (e.code) {
      case "UNAUTHORIZED":
        return "LINE認証に失敗しました。もう一度お試しください。";
      case "FORBIDDEN":
        return "WizMateの公式LINEを友だち追加してから登録してください。";
      case "SERVER":
        return "LINEとの通信に失敗しました。しばらく経ってからお試しください。";
      case "BAD_REQUEST":
        return e.message || "セッションが切れました。最初からやり直してください。";
      default:
        return "エラーが発生しました。最初からやり直してください。";
    }
  }
  return "エラーが発生しました。最初からやり直してください。";
}

const IS_STAGING = process.env.REACT_APP_ENV === "staging";

export default function LineRegisterCallback() {
  const navigate = useNavigate();
  const calledRef = useRef(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<LineRegisterErrorKind | null>(null);
  const [debugInfo, setDebugInfo] = useState<string | null>(null);
  const [debugOpen, setDebugOpen] = useState(false);
  const entrySource = readLineRegisterEntrySource();
  const retryPath = entrySource === "lineVersion" ? "/line" : "/line-register";
  const lineOfficialAccountUrl = getValidLineOfficialAccountUrl();
  const rawLineMode = sessionStorage.getItem(LINE_AUTH_MODE_KEY);
  const lineMode: "auto" | "login" | "register" =
    rawLineMode === "login" || rawLineMode === "register" ? rawLineMode : "auto";
  const requiresLegalConsent = lineMode === "register";

  const clearRegistrationState = () => {
    if (requiresLegalConsent) {
      clearPendingLegalConsent();
      sessionStorage.removeItem(LINE_AUTH_MODE_KEY);
    }
  };

  const restartRegistration = () => {
    clearRegistrationState();
    navigate(retryPath, { replace: true });
  };

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const error = params.get("error");
    const code = params.get("code");
    const state = params.get("state");

    if (IS_STAGING) {
      const lines: string[] = [
        `href: ${window.location.href}`,
        `error: ${error ?? "(none)"}`,
        `code: ${code ? code.slice(0, 8) + "…" : "(none)"}`,
        `state: ${state ? state.slice(0, 8) + "…" : "(none)"}`,
        `redirectUri: ${resolveLineRegisterCallbackUrl()}`,
      ];
      setDebugInfo(lines.join("\n"));
    }

    if (error === "access_denied" || !code || !state) {
      if (requiresLegalConsent) {
        clearPendingLegalConsent();
        sessionStorage.removeItem(LINE_AUTH_MODE_KEY);
      }
      if (IS_STAGING) {
        setErrorKind("generic");
        setErrorMessage(`パラメータ不足のため開始ページへ戻ります (error=${error ?? "none"}, code=${code ? "あり" : "なし"}, state=${state ? "あり" : "なし"})`);
        return;
      }
      navigate(retryPath, { replace: true });
      return;
    }

    const legalConsent = requiresLegalConsent ? readPendingLegalConsent() : null;
    if (requiresLegalConsent && !legalConsent) {
      clearPendingLegalConsent();
      sessionStorage.removeItem(LINE_AUTH_MODE_KEY);
      navigate(`${retryPath}?legalConsent=required`, { replace: true });
      return;
    }

    const run = async () => {
      try {
        const callbackData = await api.callbackLineRegister({
          code,
          state,
          redirectUri: resolveLineRegisterCallbackUrl(),
        });

        let completeData;
        try {
          completeData = await api.completeLineRegister({
            registrationToken: callbackData.registrationToken,
            mode: lineMode,
            ...(legalConsent ?? {}),
          });
        } catch (e: unknown) {
          if (IS_STAGING) {
            const lines: string[] = ["[completeLineRegister error]"];
            if (e instanceof AppError) {
              lines.push(`code: ${e.code}`);
              lines.push(`message: ${e.message}`);
              if (e.meta) lines.push(`meta: ${JSON.stringify(e.meta, null, 2)}`);
            } else if (e instanceof Error) {
              lines.push(`${e.name}: ${e.message}`);
            } else {
              lines.push(String(e));
            }
            setDebugInfo((prev) => (prev ? prev + "\n---\n" + lines.join("\n") : lines.join("\n")));
          }
          if (!(e instanceof AppError) || e.code === "SERVER" || e.code === "INVALID_RESPONSE" || e.code === "NETWORK") {
            logError(e);
          }
          const legalErrorMessage = registrationLegalErrorMessage(e);
          setErrorKind(legalErrorMessage ? "generic" : getLineRegisterErrorKind(e));
          setErrorMessage(legalErrorMessage ?? mapCompleteError(e));
          return;
        }

        sessionStorage.removeItem(LINE_AUTH_MODE_KEY);
        if (requiresLegalConsent) {
          clearPendingLegalConsent();
        }
        const currentAuth = storage.getAuth();
        storage.setAuth({
          userId: completeData.userId,
          secretKey: undefined,
          accessToken: completeData.accessToken,
          refreshToken: completeData.refreshToken,
          authProvider: "line",
          currentCharacterId: undefined,
          terminalIdentificationNumber: currentAuth.terminalIdentificationNumber,
        });

        await bootstrapAuth();

        const entrySource = consumeLineRegisterEntrySource();
        const lineCompletionTarget = entrySource === "lineVersion" ? "lineComplete" : "home";
        const existingUserPath = lineCompletionTarget === "lineComplete" ? "/line-register/complete" : "/home";
        if (completeData.isNewUser !== false) {
          navigate("/onboarding", { replace: true, state: { fromLine: true, lineCompletionTarget } });
        } else {
          navigate(existingUserPath, { replace: true });
        }
      } catch (e: unknown) {
        if (IS_STAGING) {
          const lines: string[] = [];
          if (e instanceof AppError) {
            lines.push(`code: ${e.code}`);
            lines.push(`message: ${e.message}`);
            if (e.meta) lines.push(`meta: ${JSON.stringify(e.meta, null, 2)}`);
          } else if (e instanceof Error) {
            lines.push(`${e.name}: ${e.message}`);
          } else {
            lines.push(String(e));
          }
          setDebugInfo((prev) => (prev ? prev + "\n---\n" + lines.join("\n") : lines.join("\n")));
        }
        if (!(e instanceof AppError) || e.code === "SERVER" || e.code === "INVALID_RESPONSE" || e.code === "NETWORK") {
          logError(e);
        }
        setErrorKind(getLineRegisterErrorKind(e));
        setErrorMessage(mapCallbackError(e));
      }
    };

    void run();
  }, [lineMode, navigate, requiresLegalConsent, retryPath]);

  const debugPanel = IS_STAGING && debugInfo ? (
    <div className="mt-4 border-t border-[#EADBC8]">
      <button
        type="button"
        onClick={() => setDebugOpen((o) => !o)}
        className="w-full py-3 text-left text-[11px] font-bold text-[#A67C52]"
      >
        [STAGING] debug {debugOpen ? "▲" : "▼"}
      </button>
      {debugOpen ? (
        <pre className="overflow-x-auto whitespace-pre-wrap break-all bg-[#F7F0EA] px-4 pb-4 text-[11px] text-[#4A3E39]">
          {debugInfo}
        </pre>
      ) : null}
    </div>
  ) : null;

  if (errorMessage) {
    const isFriendshipError = errorKind === "friendship";
    const isEntryError = errorKind === "account_not_found" || errorKind === "already_registered";

    return (
      <main className="flex min-h-screen select-none items-center justify-center bg-[#FDF8F5] px-6 text-[#4A3E39] antialiased">
        <div className="w-full max-w-sm">
          <div className="rounded-[2.2rem] border border-[#EADBC8] bg-white/70 px-8 pb-10 pt-12 shadow-[0_14px_28px_rgba(166,124,82,0.16)] backdrop-blur-sm text-center">
            <p className="text-xl font-black tracking-tight text-[#7C5A4E]">WizMate</p>
            {isFriendshipError ? (
              <>
                <div className="mt-8 rounded-2xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-4 text-left">
                  <p className="text-sm font-bold text-[#A1493E]">{LINE_FRIENDSHIP_ERROR_TITLE}</p>
                  <div className="mt-2 space-y-2 text-xs leading-relaxed text-[#A1493E]">
                    <p>{LINE_FRIENDSHIP_ERROR_BODY}</p>
                    <p>{LINE_FRIENDSHIP_ERROR_NOTE}</p>
                  </div>
                </div>
                {lineOfficialAccountUrl ? (
                  <a
                    href={lineOfficialAccountUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 block w-full rounded-2xl bg-[#06C755] px-4 py-3 text-sm font-bold text-white shadow-[0_4px_12px_rgba(6,199,85,0.3)] hover:bg-[#05b34d] active:scale-[0.99]"
                  >
                    WizMate公式LINEを友だち追加する
                  </a>
                ) : null}
                <button
                  type="button"
                  className="mt-3 w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm font-bold text-[#5D4D46] hover:bg-[#FDF8F5] active:scale-[0.99]"
                  onClick={() => {
                    clearRegistrationState();
                    navigate("/line-login", { replace: true });
                  }}
                >
                  友だち追加・ブロック解除が済んだので続ける
                </button>
                <button
                  type="button"
                  className="mt-3 w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm font-bold text-[#5D4D46] hover:bg-[#FDF8F5] active:scale-[0.99]"
                  onClick={restartRegistration}
                >
                  最初からやり直す
                </button>
              </>
            ) : isEntryError ? (
              <>
                <div className="mt-8 rounded-2xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-4 text-left">
                  {errorKind === "account_not_found" ? (
                    <>
                      <p className="text-sm font-bold text-[#A1493E]">このLINEアカウントでの登録が見つかりません。</p>
                      <p className="mt-2 text-xs text-[#A1493E]">
                        <Link to="/register" onClick={clearRegistrationState} className="font-bold underline underline-offset-2">新規登録はこちら</Link>
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-bold text-[#A1493E]">このLINEアカウントはすでに登録済みです。</p>
                      <p className="mt-2 text-xs text-[#A1493E]">
                        <Link to="/login" onClick={clearRegistrationState} className="font-bold underline underline-offset-2">ログインはこちら</Link>
                      </p>
                    </>
                  )}
                </div>
              </>
            ) : null}
            <div className={isFriendshipError || isEntryError ? "hidden" : "mt-8 rounded-2xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-4 text-left"}>
              <p className="text-sm font-bold text-[#A1493E]">認証に失敗しました</p>
              <p className="mt-1 text-xs text-[#A1493E]">
                {errorMessage || LEGAL_CONSENT_REQUIRED_MESSAGE}
              </p>
            </div>
              <button
                type="button"
                className={isFriendshipError || isEntryError ? "hidden" : "mt-6 w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm font-bold text-[#5D4D46] hover:bg-[#FDF8F5] active:scale-[0.99]"}
                onClick={restartRegistration}
              >
                最初からやり直す
              </button>
            {debugPanel}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen select-none items-center justify-center bg-[#FDF8F5] px-6 text-[#4A3E39] antialiased">
      <div className="w-full max-w-sm">
        <div className="rounded-[2.2rem] border border-[#EADBC8] bg-white/70 px-8 pb-10 pt-12 shadow-[0_14px_28px_rgba(166,124,82,0.16)] backdrop-blur-sm text-center">
          <p className="text-xl font-black tracking-tight text-[#7C5A4E]">WizMate</p>
          <div className="mt-8 rounded-2xl border border-[#EADBC8] bg-white/70 px-4 py-4">
            <p className="text-sm font-bold text-[#5D4D46]">LINEの認証を確認しています...</p>
            <div className="mt-4 flex items-center justify-center gap-1.5" aria-label="loading">
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#D1A48F] [animation-delay:-0.2s]" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#D1A48F] [animation-delay:-0.1s]" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#D1A48F]" />
            </div>
          </div>
          {debugPanel}
        </div>
      </div>
    </main>
  );
}
